import { compareRows, periodTable, teamLeaders } from './gameModel.js'
import { Callout, WinStrip } from './LiveBits.jsx'
import { Headshot, TeamMark } from './Marks.jsx'
import { recordText, sourceAge, teamSub } from './model.js'
import { clockTime } from './time.js'

// The top of the game view. P6-328b (GN-1b).
//
// Two heroes, one decision: below 900px a COMPACT stack (two team rows and the score by
// period); from 900px a HEAD-TO-HEAD (big badges, the score between them, each team's
// leaders, the period table and the team stat bars). `GameView.jsx` chooses with
// `layout.js::useMinWidth` and passes `wide`; rendering both and hiding one would put every
// name and every score in the document twice.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE STRIP AND THE CALLOUT ARE HERE WHEN THERE IS ONE, AND THERE IS NO EMPTY PLACE FOR EITHER
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's hero carries a win-probability strip and a callout under the score, for a LIVE game. P6-330b (GN-3b)
// draws them: `win` is `funModel.js::heroStrip`'s answer and `callout` is `liveCallout`'s, and each is `null` when
// there is nothing to say (no curve in preseason, nothing in the last four game minutes, a game that is not live), in
// which case nothing is drawn for it: no heading with nothing under it, no disabled control, no placeholder. The
// design says as much in its own words — *"Missing parts just don't render"* — and the page reads as finished
// without them. The five on the floor is not in the hero: it is its own section under it (`Floor.jsx`).
//
// ⚠️ THE LOGOS AND THE FACES ARE THE REAL ONES (P6-328b3), OVER THE DESIGN'S OWN STAND-INS. The design
// drew every team as a disc in its colour carrying its abbreviation, and every leader as a striped disc
// carrying initials, and called both placeholders. `Marks.jsx` draws the logo (the board's `teams[].logo`)
// and the headshot (built from the leader's `person_id`) in the same boxes, and the placeholder is what
// is there until a picture arrives and again if it never does. The logo is NOT drawn on a disc in the
// team's colour: a navy mark on a navy disc would vanish, which is why the first build kept the discs.
// `teamOf` is the board's table entry for each side (or `null`), resolved once by `GameView`.

function Dot({ kind }) {
  if (kind === 'live') return <span className="d2d-hoops__dot d2d-hoops__dot--live" aria-hidden="true" />
  if (kind === 'stale') return <span className="d2d-hoops__dot d2d-hoops__dot--stale" aria-hidden="true" />
  return null
}

function PeriodTable({ hero }) {
  const table = periodTable(hero.away, hero.home)
  const rows = [
    ['away', hero.away, table.away],
    ['home', hero.home, table.home],
  ]
  return (
    <table
      className="d2d-hoops__periods"
      data-testid="hoops-periods"
      style={{ '--hoops-periods': table.labels.length }}
    >
      <caption className="d2d-sr-only">Score by period</caption>
      <thead>
        <tr>
          <th scope="col" />
          {table.labels.map((label) => (
            <th scope="col" key={label}>
              {label}
            </th>
          ))}
          <th scope="col">T</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([which, side, points]) => (
          <tr key={which}>
            <th scope="row" data-side={which}>
              {side.tricode}
            </th>
            {points.map((value, index) => (
              <td key={table.labels[index]}>{value === null ? '–' : value}</td>
            ))}
            <td className="d2d-hoops__periodtotal">{Number.isInteger(side.score) ? side.score : '–'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function NarrowHero({ hero, teamOf }) {
  return (
    <>
      <div className="d2d-hoops__teams d2d-hoops__teams--hero">
        {[
          ['away', hero.away, hero.home],
          ['home', hero.home, hero.away],
        ].map(([which, side, other]) => (
          <div className="d2d-hoops__team d2d-hoops__team--hero" key={which} data-side={which}>
            <TeamMark team={teamOf[which]} tricode={side.tricode} which={which} size="large" />
            <div className="d2d-hoops__teamtext">
              <div className="d2d-hoops__teamname d2d-hoops__teamname--hero">{side.name ?? side.tricode}</div>
              <div className="d2d-hoops__teamsub d2d-micro">{teamSub(side, hero.kind)}</div>
            </div>
            <span
              className="d2d-hoops__score d2d-hoops__score--hero"
              data-trailing={Number.isInteger(side.score) && Number.isInteger(other.score) && side.score < other.score ? 'true' : 'false'}
            >
              {Number.isInteger(side.score) ? side.score : '–'}
            </span>
          </div>
        ))}
      </div>
      <PeriodTable hero={hero} />
    </>
  )
}

function Leaders({ which, side, players }) {
  const leaders = teamLeaders(players)
  return (
    <div className="d2d-hoops__leadercol" data-side={which}>
      <span className="d2d-hoops__rowlabel d2d-micro">{side.tricode} leaders</span>
      {leaders.map((leader) => (
        <div className="d2d-hoops__leaderrow" key={leader.key}>
          <Headshot player={leader.player} which={which} />
          <span className="d2d-hoops__leadername">{leader.player.name}</span>
          <span className="d2d-hoops__leaderstat">
            <span className="d2d-hoops__leadervalue">{leader.value}</span>
            <span className="d2d-hoops__leaderunit">{leader.unit}</span>
          </span>
        </div>
      ))}
    </div>
  )
}

function Compare({ totals }) {
  const rows = compareRows(totals?.away, totals?.home)
  return (
    <div className="d2d-hoops__compare" data-testid="hoops-compare">
      {rows.map((row) => (
        <div className="d2d-hoops__comparerow" key={row.label}>
          <span className="d2d-hoops__comparevalue" data-better={row.aBetter ? 'true' : 'false'}>
            {row.a}
          </span>
          <div className="d2d-hoops__comparemid">
            <span className="d2d-hoops__comparelabel d2d-micro">{row.label}</span>
            <div className="d2d-hoops__bar" aria-hidden="true">
              <span className="d2d-hoops__barfill d2d-hoops__barfill--away" style={{ width: `calc(${row.aShare.toFixed(1)}% - 2px)` }} />
              <span className="d2d-hoops__barfill d2d-hoops__barfill--home" />
            </div>
          </div>
          <span className="d2d-hoops__comparevalue d2d-hoops__comparevalue--home" data-better={row.hBetter ? 'true' : 'false'}>
            {row.h}
          </span>
        </div>
      ))}
    </div>
  )
}

function WideHero({ hero, game, teamOf }) {
  return (
    <>
      <div className="d2d-hoops__headtohead">
        {[
          ['away', hero.away, hero.home],
          ['home', hero.home, hero.away],
        ].map(([which, side]) => (
          <div className={`d2d-hoops__side d2d-hoops__side--${which}`} key={which} data-side={which}>
            <TeamMark team={teamOf[which]} tricode={side.tricode} which={which} size="huge" />
            <div className="d2d-hoops__sidetext">
              <div className="d2d-hoops__sidecity d2d-micro">{side.city}</div>
              <div className="d2d-hoops__sidename">{side.name ?? side.tricode}</div>
              <div className="d2d-hoops__sidesub d2d-micro">{wideSub(side, hero.kind)}</div>
            </div>
          </div>
        ))}
        <div className="d2d-hoops__bigscore" aria-label="Score">
          <span data-trailing={trailing(hero.away, hero.home)}>{scoreOf(hero.away)}</span>
          <span className="d2d-hoops__scoredash" aria-hidden="true" />
          <span data-trailing={trailing(hero.home, hero.away)}>{scoreOf(hero.home)}</span>
        </div>
      </div>
      <div className="d2d-hoops__herogrid">
        <Leaders which="away" side={hero.away} players={game?.players?.away} />
        <div className="d2d-hoops__herocenter">
          <PeriodTable hero={hero} />
          {game ? <Compare totals={game.totals} /> : null}
        </div>
        <Leaders which="home" side={hero.home} players={game?.players?.home} />
      </div>
    </>
  )
}

/** The head-to-head prints the city on its own line above the name, so a final's sub-line is the
 * record alone; a live game's is the same as everywhere (`5–2 · 7 TO · Bonus`). */
const wideSub = (side, kind) =>
  kind === 'final' ? recordText(side.record) ?? '' : teamSub(side, kind)

const scoreOf = (side) => (Number.isInteger(side.score) ? side.score : '–')
const trailing = (side, other) =>
  Number.isInteger(side.score) && Number.isInteger(other.score) && side.score < other.score ? 'true' : 'false'

/**
 * The hero. `hero` is `gameModel.js::heroModel`'s answer and `game` is the game's own object (or
 * `null` while it has not been read: the compact hero then draws from the card alone, and the
 * wide one drops the parts that need a box score rather than showing them empty). `win` and
 * `callout` are `funModel.js`'s answers for a live game (`null` for none), and `winAge` is the strip's ESPN age
 * (`heroStripAge`: `null` while ESPN's stamp is fresh, which is nearly always).
 */
export default function Hero({ hero, game, teamOf, wide, nowMs, timeZone, win = null, winAge = null, callout = null }) {
  return (
    <section
      className="d2d-hoops__hero"
      data-testid="hoops-hero"
      data-state={hero.kind}
      data-wide={wide ? 'true' : 'false'}
    >
      <div className="d2d-hoops__cardhead">
        <Dot kind={hero.kind} />
        <span className="d2d-hoops__status d2d-micro" data-testid="hoops-hero-status">
          {hero.statusLine}
        </span>
        {hero.label ? <span className="d2d-hoops__chip d2d-micro">{hero.label}</span> : null}
        <span className="d2d-hoops__src d2d-micro" data-testid="hoops-hero-source">
          {sourceAge(hero.source, hero.stampMs, nowMs)}
        </span>
      </div>

      {wide ? <WideHero hero={hero} game={game} teamOf={teamOf} /> : <NarrowHero hero={hero} teamOf={teamOf} />}

      {win === null ? null : (
        <WinStrip strip={win} away={hero.away.tricode} home={hero.home.tricode} testId="hoops-hero-wp" age={winAge} />
      )}
      {callout === null ? null : <Callout callout={callout} testId="hoops-hero-callout" large />}

      {hero.kind === 'stale' && hero.stampMs !== null ? (
        <p className="d2d-hoops__stalenote" data-testid="hoops-hero-stale">
          Stopped updating at {clockTime(hero.stampMs, timeZone)}. Everything below is as of then.
        </p>
      ) : null}
    </section>
  )
}
